import { useTranslation } from 'react-i18next';
import { AccountBalance, GetColorStatus, useDateLocale, withStyles } from 'vopay';
import { TransactionStatus } from 'UI';
import usePartnerAccountStatusLabel from '@/components/Entities/hooks/usePartnerAccountStatusLabel';
import { formatAccountDate, getStatusDateDisplay } from '../accountStatusDate';
import derivePartnerAccountStatus from '../partnerAccountStatus';
import styles from './AccountStatus.module.scss';

const cn = withStyles(styles);

type AccountStatusProps = {
  account: Pick<AccountBalance, 'AccountStatus' | 'IsActive' | 'AccountStatusDate'>;
  /**
   * How an Active account's date reads: the list says "Since <date>" like every other settled
   * status, the account card says "Activated on <date>".
   */
  activeDateCopy?: 'since' | 'activatedOn';
  /**
   * `card` right-aligns the pair and puts it on the account card's two-line rhythm, so the badge
   * lines up with the account name beside it and the date with the currency below that.
   */
  layout?: 'inline' | 'card';
};

/**
 * The status badge plus, underneath, the date a settled status was entered or the days spent in the
 * current onboarding stage (spec 5.3 / 5.5). Shared by the list's Status column and the account
 * card so the two never disagree on what a status is called or when it started.
 */
export default function AccountStatus({ account, activeDateCopy = 'since', layout = 'inline' }: AccountStatusProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.AccountStatus' });
  const { resolveStatusLabel } = usePartnerAccountStatusLabel();
  const dateLocale = useDateLocale();

  const rawStatus = derivePartnerAccountStatus(account);

  const statusDate = getStatusDateDisplay(rawStatus, account.AccountStatusDate);

  const formatDate = (date: Date) => formatAccountDate(date, dateLocale);

  return (
    <div className={cn('statusCell', layout)} data-testid="accountStatus">
      {/* The badge sets its own height, so the card's 32px line box has to come from around it. */}
      <div className={cn('badgeLine')}>
        {rawStatus === 'Blocked' ? (
          <TransactionStatus label={resolveStatusLabel('Blocked')} status="blocked" />
        ) : rawStatus === 'Active' ? (
          <TransactionStatus label={resolveStatusLabel('Active')} status="positive" />
        ) : (
          !!rawStatus && <TransactionStatus label={resolveStatusLabel(rawStatus)} status={GetColorStatus(rawStatus)} />
        )}
      </div>

      {statusDate?.kind === 'since' && (
        <span className={cn('statusMeta')}>
          {rawStatus === 'Active' && activeDateCopy === 'activatedOn'
            ? t('activatedOn', { defaultValue: 'Activated on {{date}}', date: formatDate(statusDate.date) })
            : t('statusSince', { defaultValue: 'Since {{date}}', date: formatDate(statusDate.date) })}
        </span>
      )}

      {statusDate?.kind === 'daysInStage' && (
        <span className={cn('statusMeta')}>
          {t('daysInStage', { defaultValue: '{{count}} days in this stage', count: statusDate.numDays })}
        </span>
      )}
    </div>
  );
}
